import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMButton } from "../../utils/PMButton";
import { PMNotice } from "../../utils/notifications";
import { ResourceLoading } from "../../utils/ResourceLoading";
import SimplePrompt from "../../utils/simplePrompt";
import ChecklistItemLines from "../quality/checklists/checklistItemLines";
import { EnhancedChecklistSelector } from "../service/reports/enhanced-checklist-selector";
import { useUserHasAccess, useCompanyHasModule } from "../../../routes/roleOnly";
import {
  createWorkOrderChecklist,
  deleteWorkOrderChecklist,
  fetchWorkOrderChecklist,
  updateWorkOrderChecklist,
} from "../../../api/work-order";

export function workOrderChecklistNeedsFullFetch(checklist) {
  if (!checklist?.id) return false;
  if (Array.isArray(checklist.items)) return false;
  return !Object.prototype.hasOwnProperty.call(checklist, "is_items_editable");
}

/**
 * Checklist block for work order edit (same flow as service report checklist stage).
 */
export function WorkOrderChecklistPanel({
  workOrder,
  onWorkOrderUpdated,
  canEdit = true,
}) {
  const { t } = useTranslation();
  const qualityOn = useCompanyHasModule("quality_checklists");
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [loadErr, setLoadErr] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const wo = workOrder;
  const canDeleteChecklist = useUserHasAccess({
    role: "report",
    action: "delete_checklist",
  });

  const mergeWo = useCallback(
    (patch) => {
      onWorkOrderUpdated?.(patch);
    },
    [onWorkOrderUpdated]
  );

  useEffect(() => {
    if (!wo?.id || !wo?.checklist?.id) {
      setLoadErr(false);
      return;
    }
    if (!workOrderChecklistNeedsFullFetch(wo.checklist)) return;
    if (loadErr) return;

    let cancelled = false;
    setLoadingDetail(true);

    fetchWorkOrderChecklist(wo.id, {
      fields:
        "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
    })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        if (data?.errors) {
          toast.error(t("work_orders.checklist.load_error"));
          setLoadErr(true);
          return;
        }
        if (data?.id) {
          const checklist = {
            ...data,
            items: Array.isArray(data.items) ? data.items : [],
          };
          mergeWo({ checklist });
          setLoadErr(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(t("work_orders.checklist.load_error"));
          setLoadErr(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    wo?.id,
    wo?.checklist?.id,
    wo?.checklist?.is_items_editable,
    loadErr,
    mergeWo,
    t,
  ]);

  const submitCreate = async (config) => {
    try {
      const payload = {
        work_order: { id: wo.id },
        fields: "*,checklist,uses_checklist,checklist_id,checklist_type_id",
      };
      if (config) {
        if (config.mode === "type") {
          payload.work_order.checklist_type_id = config.checklist_type_id;
          if (config.selected_item_ids?.length) {
            payload.work_order.selected_item_ids = config.selected_item_ids;
          }
        } else if (config.mode === "main_report" && config.items) {
          payload.work_order.copy_from_checklist_items = config.items;
        }
      }
      const res = await createWorkOrderChecklist(payload);
      const row = res?.data?.data ?? res?.data;
      if (row?.id && row.checklist) {
        mergeWo({
          checklist: row.checklist,
          checklist_id: row.checklist_id,
          checklist_type_id: row.checklist_type_id,
          uses_checklist: row.uses_checklist ?? true,
        });
        setSelectorOpen(false);
        toast.success(t("work_orders.checklist.created"));
      } else if (row?.errors) {
        toast.error(row.errors.join(", "));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("work_orders.checklist.create_error"));
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteWorkOrderChecklist(wo.id);
      if (res?.status === 200) {
        const row = res?.data?.data ?? res?.data;
        mergeWo({
          checklist: row?.checklist ?? null,
          checklist_id: row?.checklist_id ?? null,
          checklist_type_id: row?.checklist_type_id ?? null,
          uses_checklist: row?.uses_checklist ?? false,
        });
        setDeleteOpen(false);
        toast.success(t("work_orders.checklist.deleted"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("work_orders.checklist.delete_error"));
    }
  };

  const persistItems = async (items) => {
    try {
      const res = await updateWorkOrderChecklist(wo.id, {
        checklist: { items },
      });
      const data = res?.data;
      if (data?.id) {
        mergeWo({
          checklist: {
            ...data,
            items: Array.isArray(data.items) ? data.items : [],
          },
        });
      } else if (data?.errors) {
        toast.error(data.errors.join(", "));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("work_orders.checklist.save_error"));
    }
  };

  if (!qualityOn || !wo?.id) {
    return null;
  }

  if (!canEdit) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        {t("work_orders.checklist.read_only_hint")}
      </div>
    );
  }

  if (wo.checklist?.id) {
    if (workOrderChecklistNeedsFullFetch(wo.checklist) && loadErr) {
      return (
        <PMNotice
          title={t("work_orders.checklist.load_error_title")}
          description={t("work_orders.checklist.load_error_hint")}
        />
      );
    }
    if (workOrderChecklistNeedsFullFetch(wo.checklist) && loadingDetail) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2">
          <p className="text-sm text-gray-600">{t("work_orders.checklist.loading")}</p>
          <ResourceLoading />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {canDeleteChecklist ? (
          <>
            <div className="flex justify-end">
              <PMButton
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="bg-red-500 hover:bg-red-600"
              >
                {t("work_orders.checklist.delete")}
              </PMButton>
            </div>
            <SimplePrompt
              title={t("work_orders.checklist.delete_confirm_title")}
              description={t("work_orders.checklist.delete_confirm_body")}
              onAccept={handleDelete}
              open={deleteOpen}
              onClose={() => setDeleteOpen(false)}
            />
          </>
        ) : null}

        <ChecklistItemLines
          checklist={wo.checklist}
          items={wo.checklist?.items}
          editable={true}
          isLoading={false}
          drag={true}
          addRow={true}
          deleteRow={true}
          deviation={false}
          disabled={
            wo.checklist?.is_items_editable !== undefined &&
            !wo.checklist?.is_items_editable
          }
          type="edit"
          parentCallback={(data) => persistItems(data)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PMNotice
        title={t("work_orders.checklist.missing_title")}
        description={t("work_orders.checklist.missing_body")}
      />
      <PMButton type="button" onClick={() => setSelectorOpen(true)} className="w-full">
        {t("work_orders.checklist.choose")}
      </PMButton>
      <EnhancedChecklistSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelect={(cfg) => submitCreate(cfg)}
        mainReportChecklist={null}
        mainReport={null}
      />
    </div>
  );
}
